// ── AsyncState — one posture-driven container for loading/empty/error/success ──
// Composes the EXISTING @vjal/ui feedback primitives (PageLoading, SkeletonRows,
// EmptyState, Alert) so products stop hand-rolling the same four async surfaces.
// Drive it directly with a `status`/`posture`, or derive one with `useAsyncState`:
//
//   const status = useAsyncState({ isLoading, isError, data, error })
//   <AsyncState status={status} title="Leads" message={err} onRetry={refetch}>
//     <LeadsTable rows={data} />
//   </AsyncState>
//
// Per-app error→posture MAPPERS stay app-local — this primitive only renders the
// posture it is handed. It stamps `data-async-state` for e2e selection.

import type { ReactNode } from 'react'
import { cn } from './cn'
import { EmptyState } from './EmptyState'
import { PageLoading } from './PageLoading'
import { SkeletonRows } from './Skeleton'
import { Alert } from './Alert'
import type { AlertProps } from './Alert'
import { Button } from './Button'
import type { AsyncStatus } from './useAsyncState'

type AsyncStateErrorTone = Extract<NonNullable<AlertProps['variant']>, 'error' | 'warning'>

export interface AsyncStateProps {
  /** The posture to render. */
  status?: AsyncStatus
  /** Alias for `status` (callers that think in "posture"). `status` wins if both are set. */
  posture?: AsyncStatus
  /** Heading for the empty/error/loading surface (also the success-region label). */
  title?: ReactNode
  /** Supporting copy — the empty-state body, or the error message. */
  message?: ReactNode
  /** Optional support/error reference id, surfaced in the error posture. */
  reference?: ReactNode
  /** When provided, renders a Retry affordance in the error posture only. */
  onRetry?: () => void
  /** Error alert tone override for non-fatal/temporarily blocked API states. */
  errorTone?: AsyncStateErrorTone
  /** Label for the retry affordance in the error posture. */
  retryLabel?: ReactNode
  /** Loading look: a full-area page block (default) or table-shaped rows. */
  loadingVariant?: 'page' | 'rows'
  /** Force the empty posture even when `status` is success (e.g. resolved-but-no-rows). */
  isEmpty?: boolean
  /** Extra classes for the wrapper. */
  className?: string
  /** Success content. */
  children?: ReactNode
}

/**
 * AsyncState — renders the loading / empty / error / success surface for the
 * given posture. Returns a wrapper carrying `data-async-state="<posture>"`.
 */
export function AsyncState({
  status,
  posture,
  title,
  message,
  reference,
  onRetry,
  errorTone = 'error',
  retryLabel = 'Retry',
  loadingVariant = 'page',
  isEmpty = false,
  className = '',
  children,
}: AsyncStateProps) {
  // `status` wins; `posture` is a friendly alias; default to success.
  let effective: AsyncStatus = status ?? posture ?? 'success'
  if (effective === 'success' && isEmpty) effective = 'empty'

  let content: ReactNode
  switch (effective) {
    case 'loading':
      content =
        loadingVariant === 'rows' ? (
          <SkeletonRows />
        ) : (
          <PageLoading label={typeof title === 'string' && title ? title : 'Loading'} />
        )
      break
    case 'empty':
      content = (
        <EmptyState title={title ?? 'Nothing here yet'}>
          {message}
        </EmptyState>
      )
      break
    case 'error':
      content = (
        <Alert variant={errorTone}>
          <span className="flex flex-wrap items-center gap-2">
            <span>{message ?? title ?? 'Something went wrong.'}</span>
            {reference != null && reference !== '' && (
              // Full-strength error ink: opacity-* on small text is the class-45
              // faint-token family — it pushed this line below 4.5:1 (class-55
              // walk finding, consulting report-download 2026-07-07).
              <span className="text-xs" data-testid="product-problem-reference">Ref: {reference}</span>
            )}
            {onRetry && (
              <Button variant="secondary" size="sm" onClick={onRetry}>
                {retryLabel}
              </Button>
            )}
          </span>
        </Alert>
      )
      break
    case 'success':
    default:
      content = children
      break
  }

  return (
    <div className={cn('w-full', className)} data-async-state={effective}>
      {content}
    </div>
  )
}
